import {
  BannerCta,
  BannerMessage,
  BannerService,
} from '@services/banner.service';
import {
  ChangeDetectionStrategy,
  Component,
  SecurityContext,
  inject,
} from '@angular/core';

import { DomSanitizer } from '@angular/platform-browser';
import { Observable } from 'rxjs';
import { UtilsService } from 'src/app/core/services/utils.service';
import { NgClass, AsyncPipe, TitleCasePipe } from '@angular/common';
import { SuiButtonComponent } from '@sui/button';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-banner-container',
  templateUrl: './banner-container.component.html',
  styleUrl: './banner-container.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    SuiButtonComponent,
    AsyncPipe,
    TitleCasePipe,
    TranslatePipe,
  ],
})
export class BannerContainerComponent {
  banners$: Observable<BannerMessage[]> = this.bannerService.banners$;

  private utilsService = inject(UtilsService);
  constructor(
    private bannerService: BannerService,
    private sanitizer: DomSanitizer,
  ) {}
  dismiss(id: string) {
    this.bannerService.dismiss(id);
  }

  // Add this method in the component to route/dispatch for CTAs:
  onCta(c: BannerCta) {
    // Handle your app-specific actions here
    // e.g., if (c.action === 'auth:login') this.router.navigate(['/login']);
    // or emit to a central command bus
    console.log('CTA clicked:', c);
    switch (c.action) {
      case 'close':
        this.bannerService.dismissAll();
        break;
      case 'page:refresh':
        window.location.reload();
        break;
      case 'auth:login':
        // Navigate to login page or open login modal
        // this.router.navigate(['/login']);
        console.log('Navigate to login');
        break;
      case 'support:open':
        // Open support chat or navigate to support page
        // this.router.navigate(['/support']);
        console.log('Open support');
        break;
      default:
        console.warn('Unknown CTA action:', c.action);
    }
  }

  private expanded = new Set<string>();

  isExpanded(id: string): boolean {
    return this.expanded.has(id);
  }

  toggleDetails(id: string): void {
    if (this.expanded.has(id)) this.expanded.delete(id);
    else this.expanded.add(id);
  }

  getSafeHtml(content: string) {
    return this.sanitizer.sanitize(SecurityContext.HTML, content) || '';
  }

  /** Whether we have anything meaningful to show in the Details panel */
  hasAnyDetails(b: any): boolean {
    return !!(
      b.code ||
      b.httpCode ||
      b.requestId ||
      b.module ||
      b.action ||
      (b.fieldErrors && b.fieldErrors.length) ||
      (b.details &&
        (typeof b.details !== 'object' || Object.keys(b.details).length > 0))
    );
  }

  /** Build a clean, pretty-printed JSON for the Details panel */
  buildDetailsJson(b: any): string {
    const payload: any = {
      code: b.code ?? undefined,
      httpCode: b.httpCode ?? undefined,
      level: b.level ?? undefined,
      message: b.message ?? undefined,
      module: b.module ?? undefined,
      action: b.action ?? undefined,
      requestId: b.requestId ?? undefined,
      fieldErrors:
        b.fieldErrors && b.fieldErrors.length ? b.fieldErrors : undefined,
      details: this.normalizeDetails(b.details),
    };
    // Remove undefined keys for a tidy JSON
    Object.keys(payload).forEach(
      (k) => payload[k] === undefined && delete payload[k],
    );
    return this.prettyJson(payload);
  }

  /** Copy just the message */
  async copyMessage(b: any): Promise<void> {
    this.utilsService.copyToClipboard(b.message ?? '');
  }

  /** Copy only the details (if present), else a minimal object */
  async copyDetailsJson(b: any): Promise<void> {
    const json = this.buildDetailsJson(b);
    this.utilsService.copyToClipboard(json ?? '');
  }

  /** Copy the full normalized error object (what FE knows about the error) */
  async copyFullErrorJson(b: any): Promise<void> {
    const full = {
      level: b.level,
      message: b.message,
      code: b.code ?? undefined,
      httpCode: b.httpCode ?? undefined,
      module: b.module ?? undefined,
      action: b.action ?? undefined,
      requestId: b.requestId ?? undefined,
      fieldErrors:
        b.fieldErrors && b.fieldErrors.length ? b.fieldErrors : undefined,
      details: this.normalizeDetails(b.details),
    };
    Object.keys(full).forEach(
      (k) => (full as any)[k] === undefined && delete (full as any)[k],
    );

    this.utilsService.copyToClipboard(this.prettyJson(full));
  }

  /** Ensure details are serializable; stringify non-plain objects safely */
  private normalizeDetails(details: any): any {
    if (details == null) return undefined;
    if (
      typeof details === 'string' ||
      typeof details === 'number' ||
      typeof details === 'boolean'
    ) {
      return details;
    }
    try {
      // If it's a plain object/array, return as is; otherwise safe string
      if (
        Array.isArray(details) ||
        Object.prototype.toString.call(details) === '[object Object]'
      ) {
        return details;
      }
      return String(details);
    } catch {
      return String(details);
    }
  }

  private prettyJson(obj: any): string {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  }
}
